import { requiredFieldErrorMessage } from '@/constants/yup-error-messages'
import { IAiConversation } from '@/types/firebase'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { FC, useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

const formValuesValidationSchema = yup.object({
  title: yup.string().required(requiredFieldErrorMessage),
})

interface IAiAssistantRenameConversationFormValues {
  title: string
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  conversation: IAiConversation
  handleRenameConversation: (
    props: IAiAssistantRenameConversationFormValues,
  ) => Promise<void>
  isLoading?: boolean
}

export const AiAssistantRenameConversationModalContent: FC<Props> = ({
  conversation,
  handleRenameConversation,
  isLoading,
  className = 'w-full pt-4 pb-2 sm:max-w-[400px] flex flex-col items-center gap-8',
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  // used to apply auto-focus to input field
  // REASON: headless-ui transition used in BaseModal or use-form Controller makes delay
  // on first render and autoFocus is ignored
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const { control, handleSubmit, watch } =
    useForm<IAiAssistantRenameConversationFormValues>({
      mode: 'onSubmit',
      reValidateMode: 'onSubmit',
      defaultValues: { title: conversation.title },
      resolver: yupResolver(formValuesValidationSchema),
    })

  const currentTitle = watch('title')

  // disable submit button if the value hasn't changed or is loading
  const isSubmitDisabled = currentTitle === conversation.title || isLoading

  return (
    <div className={className} {...props}>
      {/* Controls */}
      <form
        onSubmit={handleSubmit(handleRenameConversation)}
        className="w-full flex flex-col items-center gap-8"
      >
        <Controller
          name={'title'}
          control={control}
          render={({ field, fieldState: { error } }) => {
            return (
              <div className="relative w-full flex flex-col items-start justify-center gap-6">
                <label
                  htmlFor={field.name}
                  className="w-full font-semibold text-center text-xl text-v8-mut"
                >
                  Chat Name
                </label>

                <input
                  id={field.name}
                  type={'text'}
                  required={true}
                  autoFocus={true}
                  className="block w-full rounded-md h-10 placeholder-cgray-700 text-v8-ink/100 font-normal text-base leading-6 border-v8-line focus:border-v8-blue focus:ring-v8-blue sm:text-sm placeholder-gray-400"
                  {...field}
                  ref={(el) => {
                    field.ref(el)
                    inputRef.current = el
                  }}
                />

                {error && error.message && (
                  <p className="absolute bottom-[-16px] sm:bottom-[-20px] pl-1 text-[10px] sm:text-xs text-red-600 text-start mt-2">
                    {error.message}
                  </p>
                )}
              </div>
            )
          }}
        />

        <button
          disabled={isSubmitDisabled}
          type="submit"
          className={
            'w-auto py-1 px-4 flex items-center justify-center gap-2 text-sm text-center text-white leading-4 font-semibold border border-transparent rounded bg-v8-blue disabled:opacity-70 disabled:cursor-not-allowed disabled:pointer-events-none hover:bg-white hover:border-v8-violet hover:text-v8-blue transition ease-in-out duration-500'
          }
        >
          Save
        </button>
      </form>
    </div>
  )
}
